import React, { useEffect, useState, useRef, useImperativeHandle, useMemo, forwardRef, useCallback } from 'react';

import stringify from '../utils/stableStringify';
import { unpack, pack } from 'msgpackr';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import hash from 'hash-sum';

import Chart, {
    SeriesTemplate,
    ArgumentAxis,
    CommonSeriesSettings,
    Legend,
    Series,
    ValueAxis,
    Margin,
    AdaptiveLayout,
    Size,
    Aggregation,
    Tooltip,
    Label
} from 'devextreme-react/chart';

// import { Template } from 'devextreme-react/core/template';
import AntGridBar from './AntGridBar';
// import AntGridLink from './AntGridLink';
import AntQueryStore from './AntQueryStore';
import AntConfig from '../utils/AntConfig';
import AntTime from '../utils/AntTime';
import AntString from '../utils/AntString';
import AntCondition from '../utils/AntCondition';
import AntLocalStorage from '../utils/AntLocalStorage';
import AntLocalization from '../utils/AntLocalization';
import cs from '../utils/const';
import AntSearchForm from './AntSearchForm';

import { AntGlobal, AntSession } from '../utils/AntContext';

export default forwardRef(function (props, ref) {
    const appGlobal = AntGlobal();
    const location = useLocation();

    if (props.API && !props.API.entityDisplay) {
        if (appGlobal && appGlobal.appConfig && appGlobal.appConfig.Services) {
            let found = appGlobal.appConfig.Services.find((s) => s.name === props.API.entityName);
            if (found) props.API.init({ entityDisplay: found.display });
        }
    }

    const tableName = props.table ? props.table : props.API ? props.API.entityName : '';

    const config = useMemo(() => {
        let found = appGlobal.tableConfigs?.find((t) => t.tableName === tableName);
        if (found) return found.config;

        let cnf = new AntConfig(appGlobal, tableName);
        cnf.configFields(props.fields);

        if (appGlobal.tableConfigs instanceof Array === false) appGlobal.tableConfigs = [];

        appGlobal.tableConfigs.push({
            tableName: tableName,
            config: cnf
        });
        return cnf;
    }, []);

    const currencyFormat = useMemo(() => {
        let strformat = AntLocalization.getCurrencyFormat();
        return strformat;
    }, []);

    const compName = props.storageKey
        ? props.storageKey
        : props.name
        ? props.name
        : props.API && props.API.entityName
        ? props.API.entityName
        : props.entityName
        ? props.entityName
        : props.url
        ? props.url
        : undefined;

    const getCachingStates = useCallback(() => {
        var defaultObj = { searchText: '', filteredGroup: null };

        var path = location.pathname + '-' + compName;

        if (path) {
            var data = localStorage.getItem(cs.System_Code + '-chart-states');

            if (data) data = JSON.parse(data);
            if (data) {
                var hashKey = hash(String(path));

                if (data[hashKey]) defaultObj = data[hashKey];
            }
        }

        return defaultObj;
    });

    const saveCachingStates = useCallback(() => {
        var path = location.pathname + '-' + compName;

        if (path) {
            var data = localStorage.getItem(cs.System_Code + '-chart-states');
            if (data) data = JSON.parse(data);
            if (!data) data = {};

            let obj = {
                searchText: searchText !== null && searchText !== undefined ? String(searchText) : '',
                filteredGroup: filteredGroup.current ? stringify(filteredGroup.current) : null
            };
            if (obj.searchText === '' || obj.searchText === 'undefined') delete obj.searchText;
            if (!obj.filteredGroup) delete obj.filteredGroup;

            var hashKey = hash(String(path));
            data[hashKey] = obj;

            localStorage.setItem(cs.System_Code + '-chart-states', String(stringify(data)));
        }
    });

    const initDefaultFilteredGroup = () => {
        if (mainActions instanceof Array) {
            var item = mainActions.find((i) => i.widget === 'ButtonGroup');
            if (item && item.items instanceof Array && item.items.length > 0) {
                if (props.filteredGroup) {
                    var found = item.items.find((i) => i.name === props.filteredGroup);
                    if (found) return found;
                }
                if (item.defaultSelect) {
                    var found = item.items.find((i) => i.name === item.defaultSelect);
                    if (found) return found;
                }
                if (item && item.items instanceof Array) {
                    var found = item.items.find((i) => i.default === true);
                    if (found) return [found];
                }
                if (item.items.length > 0) {
                    return item.items[0];
                }
            }
        }

        return null;
    };

    const beginUpdate = () => {
        if (chart.current && chart.current.instance) {
            chart.current.instance.beginUpdate();
        }
    };

    const endUpdate = () => {
        if (chart.current && chart.current.instance) {
            chart.current.instance.updateDimensions();
            chart.current.instance.endUpdate();
        }
    };

    const initDataSource = () => {
        if (props.API && !props.API.customData) {
            if (
                props &&
                (props.argumentField === 'monthAndYear' ||
                    props.argumentField === 'monthInYear' ||
                    props.argumentField === 'dayInYear' ||
                    props.argumentField === 'monthAndYearAndDay')
            ) {
                var fieldName = props.queryFields instanceof Array && props.queryFields.length === 1 ? props.queryFields[0] : 'created_at';

                props.API.customData = (data) => {
                    if (data[fieldName]) {
                        var date = AntTime.fromUnix(data[fieldName]).toDate();
                        var year = AntTime.fromUnix(data[fieldName]).format('YYYY');
                        var month = AntTime.fromUnix(data[fieldName]).format('MM');
                        var day = AntTime.fromUnix(data[fieldName]).format('DD');

                        data.colDate = date;
                        data.year = AntString.format('{0}', year);
                        data.monthAndYear = AntString.format('{0}/{1}', month, year);
                        data.monthInYear = AntString.format('{0}/{1}', month, year);
                        data.monthAndYearAndDay = AntString.format('{0}/{1}/{2}', day, month, year);
                        data.dayInYear = AntString.format('{0}/{1}/{2}', day, month, year);
                    }
                    return data;
                };
            }
        }

        return new AntQueryStore({
            key: props.keyExpr,
            API: props.API,
            url: props.url,
            loadFunc: props.loadFunc,

            getSearchConditions: dsGetSearchConditions,
            getParentSearchConditions: dsGetParentSearchConditions,
            getFilteredGroupConditions: dsGetFilteredGroupConditions,
            getQueryFields: dsGetQueryFields,
            getSearchText: dsGetSearchText,
            isAllowQuery: isAllowQuery,
            onGotData: onDatasourceGotData,
            onLoading: onDatasourceLoading,
            onLoaded: onDatasourceLoaded
        });
    };

    const dsGetSearchText = () => {
        return searchText;
    };

    const dsGetQueryFields = useCallback((opts) => {
        var ar = [];
        if (props.argumentField) ar.push(props.argumentField);

        if (props.series1 && props.series1.valueField) ar.push(props.series1.valueField);
        if (props.series2 && props.series2.valueField) ar.push(props.series2.valueField);
        if (props.series3 && props.series3.valueField) ar.push(props.series3.valueField);

        if (props.queryFields instanceof Array) ar.push(...props.queryFields);

        if (props.getQueryFields) {
            var fields = props.getQueryFields();
            if (fields instanceof Array) ar.push(...fields);
        }

        ar = ar
            .filter((v) => Boolean(v))
            .filter((v, index, self) => {
                return self.indexOf(v) === index;
            });

        ar = ar
            .filter((v) => Boolean(v))
            // .filter((v) => Boolean(v.alias))
            .filter((v, index, self) => {
                return self.indexOf(v) === index;
            });

        return ar;
    });

    const dsGetSearchConditions = () => {
        return searchConditions.current ? JSON.parse(stringify(searchConditions.current)) : {};
    };

    const dsGetParentSearchConditions = () => {
        return searchParentConditions ? JSON.parse(stringify(searchParentConditions)) : {};
    };

    const dsGetFilteredGroupConditions = () => {
        if (filteredGroup.current && filteredGroup.current.searchConditions) return filteredGroup.current.searchConditions;

        return {};
    };

    const _baseInitMenuItem = useCallback((obj) => {
        if (!obj) return null;

        let strText = obj.label
            ? AntLocalization.translateText(AntCondition.getValue({}, obj.label))
            : AntLocalization.translateText(AntCondition.getValue({}, obj.text));

        var current = {
            valueExpr: obj.valueExpr,
            displayExpr: obj.displayExpr,
            icon: obj.icon,
            text: strText,

            value: obj.value,
            width: obj.width,
            stylingMode: obj.stylingMode ? obj.stylingMode : 'text'
        };

        if (obj.items instanceof Array && obj.items.length > 0) {
            current.items = [];

            obj.items.forEach((i) => {
                var child = _baseInitMenuItem(i);
                if (child) current.items.push(child);
            });
        }
        return current;
    });

    const baseInitMenuItem = useCallback((mItem) => {
        if (!mItem || !mItem.isMenu) return;

        var m = _baseInitMenuItem(mItem);
        if (m) {
            return {
                ...mItem,
                widget: 'Menu',
                location: mItem.location,
                items: [m]
            };
        }
    });

    const initActions = () => {
        var actions = props.actionItems instanceof Array ? props.actionItems : [];
        if (actions instanceof Array)
            actions = actions.map((c) => {
                var nItem = JSON.parse(stringify(c));

                nItem.text = AntLocalization.translateText(nItem.text);
                if (nItem.isMenu) {
                    nItem = baseInitMenuItem(nItem);
                } else if (nItem.widget === 'ButtonGroup') {
                    if (nItem.items instanceof Array) {
                        nItem.items = nItem.items.filter((item) => {
                            return AntCondition.isVisible(props.parentData, item);
                        });
                    }

                    if (nItem.defaultSelect && (typeof nItem.defaultSelect === 'object' || nItem.defaultSelect instanceof Array))
                        nItem.defaultSelect = AntCondition.getValue(props.parentData, nItem.defaultSelect);
                }

                return nItem;
            });

        actions = actions.filter((item) => {
            var isVisible = AntCondition.isVisible(props.parentData, item);
            if (
                isVisible &&
                typeof item.visible === 'object' &&
                item.visible &&
                typeof item.visible.whenAction === 'object' &&
                item.visible.whenAction
            ) {
                isVisible = AntCondition.isVisible(searchConditions.current, {
                    visible: {
                        when: {
                            ...item.visible.whenAction
                        }
                    }
                });
            }
            return isVisible;
        });
        actions.forEach((item) => {
            item.editable = AntCondition.isEditable(props.parentData, item);
        });
        return actions;
    };

    const generateSearchConditions = (opts) => {
        let objSearch = {};

        if (props.searchConditions) {
            if (typeof props.searchConditions === 'function') {
                var obj = props.searchConditions();
                if (obj) objSearch = { ...objSearch, ...obj };
            } else objSearch = { ...objSearch, ...props.searchConditions };
        }

        objSearch = {
            ...objSearch,
            ...formSearchConditions
        };

        if (props.actionItems instanceof Array) {
            props.actionItems.forEach((item) => {
                if (item.name && item.widget !== 'Button' && item.widget !== 'Menu' && objSearch[item.name] === undefined)
                    objSearch[item.name] = item.defaultValue !== undefined ? item.defaultValue : false;
            });
        }

        if (actionBar.current) {
            var obj = actionBar.current.getSearchConditions();
            if (obj)
                objSearch = {
                    ...objSearch,
                    ...obj
                };
        }

        if (props.searchFields instanceof Array && props.searchFields.length > 0) {
            Object.keys(objSearch).map((k) => {
                var found = props.searchFields.find((f) => typeof f === 'object' && f.alias && f.field === k);
                if (found) {
                    objSearch[found.alias] = objSearch[k];
                    delete objSearch[k];
                }
            });

            Object.keys(objSearch).map((k) => {
                var found = props.searchFields.find(
                    (f) =>
                        f === k ||
                        f === k + '_oneMonth' ||
                        f === k + '_oneYear' ||
                        f === k + '_startDate' ||
                        f === k + '_startMonth' ||
                        f === k + '_startYear' ||
                        f === k + '_endDate' ||
                        f === k + '_endMonth' ||
                        f === k + '_endYear' ||
                        k === f + '_oneMonth' ||
                        k === f + '_oneYear' ||
                        k === f + '_startDate' ||
                        k === f + '_startMonth' ||
                        k === f + '_startYear' ||
                        k === f + '_endDate' ||
                        k === f + '_endMonth' ||
                        k === f + '_endYear' ||
                        (typeof f === 'object' && f.alias === k)
                );

                if (!found && props.actionItems instanceof Array) found = props.actionItems.find((f) => f.name === k);

                if (!found) objSearch[k] = undefined;
            });
        }

        delete objSearch.filterBySelected;

        Object.keys(objSearch).map((k) => {
            if (objSearch[k] !== null && typeof objSearch[k] === 'object') objSearch[k] = AntCondition.getValueFromSession(objSearch[k]);
        });

        Object.keys(objSearch).map((k) => {
            if (objSearch[k] === null || objSearch[k] === '') objSearch[k] = undefined;
        });

        Object.keys(objSearch).forEach((key) => {
            if (objSearch[key] === undefined) {
                delete objSearch[key];
            }
        });

        if (props.isSearching) objSearch.isSearching = true;

        searchConditions.current = objSearch;

        autoReload();

        return objSearch;
    };

    const hasFilterGroups = useCallback(() => {
        if (props.actionItems instanceof Array) {
            var found = props.actionItems.find((i) => i.widget === 'ButtonGroup');
            if (found) return true;
        }

        return false;
    });

    const isAllowQuery = () => {
        if (props.searchConditionsRequired && (!searchConditions.current || Object.keys(searchConditions.current).length === 0)) return false;

        if (props.isAllowQuery && typeof props.isAllowQuery === 'function') return props.isAllowQuery();

        return true;
    };

    const getKeyField = useCallback(() => {
        return props.keyExpr ? props.keyExpr : 'id';
    });

    const getCachedData = () => {
        if (dataSource.current) return dataSource.current.getCachedData();
        return null;
    };

    const reload = async (opts) => {
        refresh(true);
    };

    const refresh = (changesOnly) => {
        if (chart.current && chart.current.instance) {
            chart.current.instance.refresh(changesOnly);
        }
    };

    const repaint = () => {
        if (chart.current && chart.current.instance) {
            chart.current.instance.repaint();
        }
    };

    const refreshAndPaint = async () => {
        if (chart.current && chart.current.instance) {
            await dataSource.current.willGetLastResult();
            chart.current.instance.refresh(true);
        }
    };

    const isDataLoading = () => {
        return Boolean(loading);
    };

    const isDataLoaded = () => {
        return Boolean(loaded.current);
    };

    const onDatasourceLoading = () => {
        loading.current = true;
    };

    const onDatasourceLoaded = (dataRows) => {
        loading.current = false;
        loaded.current = true;

        autoResetUI();

        if (actionBar.current) actionBar.current.runCounting();

        if (props.onLoaded) props.onLoaded(dataRows);

        var sTextValue = dataRows && dataRows.conditions instanceof Array ? dataRows.conditions.find((c) => c.name === 'searchText') : null;
        sTextValue = sTextValue ? sTextValue.value : null;

        var totalCount = dataRows ? dataRows.totalCount : null;
        var rows = dataRows && dataRows.data instanceof Array ? dataRows.data : [];
        var row = rows instanceof Array && rows.length === 1 && totalCount === 1 ? rows[0] : null;

        if (actionBar.current) {
            actionBar.current.resetItems({
                shouldCount: true
            });
        }

        if (rows.length <= 0) {
            repaint();
            setTimeout(() => {
                repaint();
            }, 500);
        }

        //  else if (searchText) {
        //     if (actionBar.current) actionBar.current.setFilterBySelected(false);
        // }
    };

    const onDatasourceGotData = (data) => {};

    const resetSelectedActionGroups = (selectedGroup) => {
        if (actionBar.current) {
            actionBar.current.resetSelectGroups(selectedGroup);
        }
    };

    const onFilteredByGroup = (e) => {
        if (props.onFilteredByGroupChanged) props.onFilteredByGroupChanged(e);
        setFilteredGroup(e);
    };

    const onSearchText = (text) => {
        setSearchText(text);
        if (props.onSearchText) props.onSearchText(text);
    };

    const onStartSearchText = () => {
        setSearchBarFocused(true);
    };

    const onAction = (e) => {
        if (e.name) generateSearchConditions();

        if (props.onAction && typeof props.onAction === 'function') props.onAction(e);

        if (e.name) {
            generateSearchConditions();
            // searchConditions.current = Object.assign(searchConditions.current, { [e.name]: e.value });
            // // setSearchConditions({ ...searchConditions, [e.name]: e.value });
        }
    };

    const onContentReady = (e) => {
        // resetUI();
    };
    const onOptionChanged = (e) => {
        if (props.onOptionChanged) props.onOptionChanged(e);
    };

    const resetUI = () => {
        // if (loading.current || !grid.current || !grid.current.instance || !grid.current.instance.option('visible')) {
        //     setTimeout(() => {
        //         resetUI();
        //     }, 500);
        //     return;
        // }
        // beginUpdate();
        // endUpdate();
    };

    const autoResetUI = () => {
        resetUI();
        if (autoResetUITimer.current) clearInterval(autoResetUITimer.current);
        autoResetUITimer.current = setInterval(() => {
            resetUI();
        }, 5 * 1000);

        saveCachingStates();
    };

    const autoReload = () => {
        reload();
        if (autoRefreshTimer.current) clearInterval(autoRefreshTimer.current);
        autoRefreshTimer.current = setInterval(() => {
            reload();
        }, 30 * 1000);
    };

    const forceReload = () => {
        generateSearchConditions();

        autoReload();

        setTimeout(() => {
            resetUI();
        }, 500);
    };

    const defaultStates = useMemo(() => getCachingStates(), []);

    const loading = useRef(false);
    const loaded = useRef(false);

    const chart = useRef();
    const actionBar = useRef();
    const afterActionRefreshTimeout = useRef();
    const autoRefreshTimer = useRef();
    const autoResetUITimer = useRef();
    const filteredGroup = useRef();

    const searchParentConditions =
        props.FKField && props.parentData && typeof props.parentData === 'object'
            ? {
                  [props.FKField]: props.parentData[props.parentIDField ? props.parentIDField : 'code']
              }
            : {};

    const searchConditions = useMemo(() => {
        return {};
    }, []);
    const [searchText, setSearchText] = useState(defaultStates.searchText);

    const [searchBarFocused, setSearchBarFocused] = useState(false);

    const dataSource = useRef(initDataSource());

    const mainActions = useMemo(() => initActions(), []);

    const [showSearchForm, setShowSearchForm] = useState(false);
    const [formSearchConditions, setFormSearchConditions] = useState({ ...props.searchConditions });

    if (!filteredGroup.current)
        filteredGroup.current = defaultStates.filteredGroup ? JSON.parse(defaultStates.filteredGroup) : initDefaultFilteredGroup();

    const forceRender = useCallback(() => {
        setShouldRender((v) => v + 1);
    }, []);

    const [shouldRender, setShouldRender] = useState(1);
    useImperativeHandle(ref, () => ({
        forceReload: forceReload,
        resetSelectedActionGroups: resetSelectedActionGroups,
        refresh: refresh,
        getKeyField: getKeyField,
        forceRender: forceRender
    }));

    useEffect(() => {
        forceReload();
    }, []);

    useEffect(() => {
        autoReload();
    }, [searchText]);

    useEffect(() => {
        generateSearchConditions();
    }, [props.searchConditions, props.actionItems, formSearchConditions]);

    var seriesCount = 0;
    if (props.series1) seriesCount++;
    if (props.series2) seriesCount++;
    if (props.series3) seriesCount++;
    var showLegend = props.showLegend === true && (seriesCount > 1 || props.seriesTemplate);

    const customizeTooltip = useCallback((arg) => {
        if (arg.seriesName && props.series1?.name === arg.seriesName && props.series1?.hideTooltip) {
            return { text: null };
        }
        if (arg.seriesName && props.series2?.name === arg.seriesName && props.series2?.hideTooltip) {
            return { text: null };
        }
        if (arg.seriesName && props.series3?.name === arg.seriesName && props.series3?.hideTooltip) {
            return { text: null };
        }
        if (arg.seriesName && props.series4?.name === arg.seriesName && props.series4?.hideTooltip) {
            return { text: null };
        }
        return { text: arg.valueText };
    }, []);

    return (
        <React.Fragment>
            {shouldRender && Boolean(props.search) && (
                <div style={{ display: showSearchForm ? 'block' : 'none' }}>
                    <p />
                    <AntSearchForm
                        formData={formSearchConditions}
                        shouldRender={shouldRender + (props.shouldRender ? props.shouldRender : 1)}
                        prepareConditions={props.search ? props.search.prepareConditions : undefined}
                        default={props.search ? props.search.default : undefined}
                        API={props.search && props.search.API ? props.search.API : props.API}
                        table={props.search && props.search.table ? props.search.table : props.table}
                        entityName={props.API.entityName ? props.API.entityName : props.entityName}
                        entityDisplay={props.API.entityDisplay ? props.API.entityDisplay : props.entityDisplay}
                        colCount={props.search && props.search.colCount !== undefined ? props.search.colCount : undefined}
                        colCountDefault={props.search ? props.search.colCountDefault : undefined}
                        // onConditionsChanged={setFormSearchConditions}
                        onFormDataChanged={setFormSearchConditions}
                        fields={props.fields}
                        items={props.search && props.search.items instanceof Array ? props.search.items : []}
                    />
                    <p />
                </div>
            )}

            {shouldRender && props.showActionBar != false && (
                <AntGridBar
                    ref={actionBar}
                    shouldRender={shouldRender + (props.shouldRender ? props.shouldRender : 1)}
                    dataSource={dataSource.current}
                    showRefresh={Boolean(props.showRefresh)}
                    showFilter={Boolean(props.search)}
                    onFilterClick={() => setShowSearchForm((v) => !Boolean(v))}
                    showSearchBar={props.showSearchBar}
                    searchBarWidth={props.searchBarWidth}
                    locationSearchBar={props.locationSearchBar ? props.locationSearchBar : 'after'}
                    searchText={searchText}
                    autoFocusSearchBar={searchBarFocused ? false : props.autoFocusSearchBar}
                    onRefreshClick={reload}
                    onSearchText={onSearchText}
                    onStartSearchText={onStartSearchText}
                    onAction={onAction}
                    onFilteredByGroup={(gr) => {
                        filteredGroup.current = gr;
                        generateSearchConditions();
                    }}
                    items={mainActions}
                    filteredGroup={filteredGroup.current ? filteredGroup.current.name : null}
                />
            )}
            {shouldRender && !props.hideHeaderMargin && props.showActionBar === false && <p />}

            {shouldRender && (
                <Chart
                    ref={chart}
                    shouldRender={shouldRender + (props.shouldRender ? props.shouldRender : 1)}
                    // palette="Soft"
                    // noDataText="Không có dữ liệu"
                    noDataText=""
                    className={'dx-card wide-card'}
                    rotated={props.rotated}
                    dataSource={dataSource.current ? dataSource.current.instance() : null}
                    palette={props.palette}
                    opacity={0.1}
                >
                    <Tooltip enabled={true} format={currencyFormat} customizeTooltip={customizeTooltip} />

                    <Margin top={20} bottom={20} left={10} right={10} />
                    <Size height={props.height !== undefined ? props.height : showLegend ? 300 : 253} />
                    {/* <AdaptiveLayout width={450} /> */}
                    <CommonSeriesSettings
                        argumentField={props.argumentField}
                        type={'bar'}
                        barWidth={30}
                        // barPadding={0.2}
                        // opacity={0.2}
                        aggregation={{ enabled: true, method: 'sum' }}
                        hoverStyle={{ color: '#facb86' }}
                        {...props.commonSeriesSettings}
                    />
                    <ArgumentAxis
                        aggregateByCategory={true}
                        tick={{ visible: false }}
                        grid={{ opacity: 0.4 }}
                        {...props.argumentAxis}
                        label={{
                            overlappingBehavior: 'rotate',
                            rotationAngle: -30,
                            font: { size: 10 },

                            customizeText: (e) => {
                                if (props.argumentFormat === 'dd/MM') {
                                    if (String(e.valueText).includes('/')) {
                                        var ar = String(e.valueText).split('/');
                                        ar.pop();
                                        return ar.join('/');
                                    }
                                }
                                return e.valueText;
                            },
                            ...props.argumentAxis?.label
                        }}
                    >
                        {/* <Label
                            overlappingBehavior="rotate"
                            rotationAngle={-30}
                            font={{ size: 10 }}
                            customizeText={(e) => {
                                if (props.argumentFormat === 'dd/MM') {
                                    if (String(e.valueText).includes('/')) {
                                        var ar = String(e.valueText).split('/');
                                        ar.pop();
                                        return ar.join('/');
                                    }
                                }
                                return e.valueText;
                            }}
                            {...props.argumentAxis?.label}
                        /> */}
                    </ArgumentAxis>
                    {props.seriesTemplate && (
                        <SeriesTemplate
                            {...props.seriesTemplate}
                            nameField={props.seriesTemplate.nameField ? props.seriesTemplate.nameField : undefined}
                            // customizeSeries={() => {
                            //     return {
                            //         cornerRadius: 2,
                            //         ...props.seriesTemplate.config
                            //     };
                            // }}
                        >
                            <Aggregation enabled={true} method={props.seriesTemplate.calcType ? props.seriesTemplate.calcType : 'sum'} />
                        </SeriesTemplate>
                    )}
                    {props.series1 && (
                        <Series
                            {...props.series1}
                            // name={props.series1.name ? props.series1.name : undefined}
                            name={props.series1.name ? AntLocalization.translateText(props.series1.name) : undefined}
                            valueField={props.series1.valueField ? props.series1.valueField : undefined}
                            axis="axis1"
                            type={props.series1.type ? props.series1.type : 'bar'}
                            color={props.series1.color}
                            // label={props.series1.label ? AntLocalization.translateText(props.series1.label) : undefined}
                            barWidth={props.series1.barWidth !== undefined ? props.series1.barWidth : 30}
                            cornerRadius={props.series1.cornerRadius ? props.series1.cornerRadius : props.series1.type !== 'bar' ? 0 : 2}
                            label={{
                                ...props.series1.label,
                                format: currencyFormat,
                                customizeText:
                                    props.series1.label?.customizeText === 'Currency-Short'
                                        ? (e) => {
                                              let v = e.originalValue;
                                              let value = parseFloat(v);
                                              if (value > 1000000) {
                                                  value = Math.floor(value / 1000000);
                                                  return (
                                                      AntLocalization.translateInteger(value) + AntLocalization.translateText({ vi: 'tr', en: 'M', fr: 'M', es: 'M', id: 'M', ja: 'M', zh: 'M', ko: 'M', hi: 'M', ar: 'M' })
                                                  );
                                              } else if (value > 1000) {
                                                  value = Math.floor(value / 1000);
                                                  return AntLocalization.translateInteger(value) + 'K';
                                              }

                                              return e.valueText;
                                          }
                                        : props.series1.label?.customizeText
                            }}
                        >
                            <Aggregation enabled={true} method={props.series1.calcType ? props.series1.calcType : 'sum'} />
                        </Series>
                    )}
                    {props.series2 && (
                        <Series
                            {...props.series2}
                            // name={props.series2.name ? props.series2.name : undefined}
                            name={props.series2.name ? AntLocalization.translateText(props.series2.name) : undefined}
                            valueField={props.series2.valueField ? props.series2.valueField : undefined}
                            axis="axis1"
                            type={props.series2.type ? props.series2.type : 'bar'}
                            color={props.series2.color}
                            // label={props.series2.label ? AntLocalization.translateText(props.series2.label) : undefined}
                            barWidth={props.series2.barWidth !== undefined ? props.series2.barWidth : 30}
                            cornerRadius={props.series2.cornerRadius ? props.series2.cornerRadius : props.series2.type !== 'bar' ? 0 : 2}
                            label={{
                                ...props.series2.label,
                                format: currencyFormat,
                                customizeText:
                                    props.series2.label?.customizeText === 'Currency-Short'
                                        ? (e) => {
                                              let v = e.originalValue;
                                              let value = parseFloat(v);
                                              if (value > 1000000) {
                                                  value = Math.floor(value / 1000000);
                                                  return (
                                                      AntLocalization.translateInteger(value) + AntLocalization.translateText({ vi: 'tr', en: 'M', fr: 'M', es: 'M', id: 'M', ja: 'M', zh: 'M', ko: 'M', hi: 'M', ar: 'M' })
                                                  );
                                              } else if (value > 1000) {
                                                  value = Math.floor(value / 1000);
                                                  return AntLocalization.translateInteger(value) + 'K';
                                              }

                                              return e.valueText;
                                          }
                                        : props.series2.label?.customizeText
                            }}
                        >
                            <Aggregation enabled={true} method={props.series2.calcType ? props.series2.calcType : 'sum'} />
                        </Series>
                    )}
                    {props.series3 && (
                        <Series
                            {...props.series3}
                            // name={props.series3.name ? props.series3.name : undefined}
                            name={props.series3.name ? AntLocalization.translateText(props.series3.name) : undefined}
                            valueField={props.series3.valueField ? props.series3.valueField : undefined}
                            axis="axis1"
                            type={props.series3.type ? props.series3.type : 'bar'}
                            color={props.series3.color}
                            barWidth={props.series3.barWidth !== undefined ? props.series3.barWidth : 30}
                            cornerRadius={props.series3.cornerRadius ? props.series3.cornerRadius : props.series3.type !== 'bar' ? 0 : 2}
                            label={{
                                ...props.series3.label,
                                format: currencyFormat,
                                customizeText:
                                    props.series3.label?.customizeText === 'Currency-Short'
                                        ? (e) => {
                                              let v = e.originalValue;
                                              let value = parseFloat(v);
                                              if (value > 1000000) {
                                                  value = Math.floor(value / 1000000);
                                                  return (
                                                      AntLocalization.translateInteger(value) + AntLocalization.translateText({ vi: 'tr', en: 'M', fr: 'M', es: 'M', id: 'M', ja: 'M', zh: 'M', ko: 'M', hi: 'M', ar: 'M' })
                                                  );
                                              } else if (value > 1000) {
                                                  value = Math.floor(value / 1000);
                                                  return AntLocalization.translateInteger(value) + 'K';
                                              }

                                              return e.valueText;
                                          }
                                        : props.series3.label?.customizeText
                            }}
                        >
                            <Aggregation enabled={true} method={props.series3.calcType ? props.series3.calcType : 'sum'} />
                        </Series>
                    )}
                    {props.series4 && (
                        <Series
                            {...props.series4}
                            // name={props.series4.name ? props.series4.name : undefined}
                            name={props.series4.name ? AntLocalization.translateText(props.series4.name) : undefined}
                            valueField={props.series4.valueField ? props.series4.valueField : undefined}
                            axis="axis1"
                            type={props.series4.type ? props.series4.type : 'bar'}
                            color={props.series4.color}
                            barWidth={props.series4.barWidth !== undefined ? props.series4.barWidth : 30}
                            cornerRadius={props.series4.cornerRadius ? props.series4.cornerRadius : props.series4.type !== 'bar' ? 0 : 2}
                            label={{
                                ...props.series4.label,
                                customizeText:
                                    props.series4.label?.customizeText === 'Currency-Short'
                                        ? (e) => {
                                              let v = e.originalValue;
                                              let value = parseFloat(v);
                                              if (value > 1000000) {
                                                  value = Math.floor(value / 1000000);
                                                  return (
                                                      AntLocalization.translateInteger(value) + AntLocalization.translateText({ vi: 'tr', en: 'M', fr: 'M', es: 'M', id: 'M', ja: 'M', zh: 'M', ko: 'M', hi: 'M', ar: 'M' })
                                                  );
                                              } else if (value > 1000) {
                                                  value = Math.floor(value / 1000);
                                                  return AntLocalization.translateInteger(value) + 'K';
                                              }

                                              return e.valueText;
                                          }
                                        : props.series4.label?.customizeText
                            }}
                        >
                            <Aggregation enabled={true} method={props.series4.calcType ? props.series4.calcType : 'sum'} />
                        </Series>
                    )}
                    <ValueAxis
                        name="axis1"
                        {...props.valueAxis}
                        position={props.valueAxis && props.valueAxis.position ? props.valueAxis.position : 'left'}
                        showZero={true}
                        valueMarginsEnabled={false}
                        tick={{ visible: false }}
                        grid={{ opacity: 0.4 }}
                        label={{ format: currencyFormat }}
                    />
                    {/* <Label
                            overlappingBehavior="stagger"
                            // customizeText={customizePercentageText}
                        /> */}
                    {/* </ValueAxis> */}
                    <Legend verticalAlignment="top" horizontalAlignment="center" {...props.legend} visible={showLegend} />
                </Chart>
            )}
        </React.Fragment>
    );
});
